import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Drawer, DrawerContent } from "ui/Drawer";
import Modal from "ui/Modal";
import { useUser } from "util/UserContext";
import IssueSearch, { Suggestion } from "components/Issues/IssueSearch";
import EntityAvatar from "components/Entities/EntityAvatar";
import TextEditor from "components/TextEditor";
import AddButton from "ui/AddButton";
import EntityPrivacy from "components/Entities/EntityPrivacy";
import { PrivacyLevel } from "util/userUtils";
import { createResteep, ResteepSourceEntityType } from "lib/api/placements";
import { useIssueFromSuggestion } from "hooks/useIssueFromSuggestion";
import { apiRoutes } from "util/apiRoutes";
import api from "config/axiosConfig";
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi";

interface ResteepModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceEntityType: ResteepSourceEntityType;
  sourceEntityId: number | string;
  sourceEntityTitle?: string;
}

/**
 * Modal for creating a resteep (placement-based).
 * Users choose a target (issue or their own profile) and optionally add commentary.
 */
const ResteepModal: React.FC<ResteepModalProps> = ({
  isOpen,
  onClose,
  sourceEntityType,
  sourceEntityId,
  sourceEntityTitle = "",
}) => {
  const { userIsMobile, currentUser, awaitingUser } = useUser();
  const ui = getStoryUiStrings(resolveStoryLocale());

  const [selectedIssue, setSelectedIssue] = useState<Suggestion | null>(null);
  const [isProfileSelected, setIsProfileSelected] = useState(false);
  const [content, setContent] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [privacy, setPrivacy] = useState<PrivacyLevel>(
    currentUser?.properties?.privacy_content_default_audience?.value || "public"
  );

  const { ensureIssue } = useIssueFromSuggestion();
  const [resolvedIssueId, setResolvedIssueId] = useState<number | null>(null);
  const [isResolvingIssue, setIsResolvingIssue] = useState(false);
  const resolveRequestIdRef = useRef<number>(0);

  useEffect(() => {
    if (isOpen) {
      setSelectedIssue(null);
      setIsProfileSelected(false);
      setContent("");
      setError(null);
      setPrivacy(
        currentUser?.properties?.privacy_content_default_audience?.value || "public"
      );
      setResolvedIssueId(null);
      setIsResolvingIssue(false);
      resolveRequestIdRef.current = 0;
    }
  }, [isOpen, currentUser]);

  useEffect(() => {
    if (awaitingUser) return;
    if (currentUser?.properties?.privacy_content_default_audience) {
      setPrivacy(currentUser.properties.privacy_content_default_audience.value);
    } else {
      setPrivacy("public");
    }
  }, [currentUser, awaitingUser]);

  const normalizeIssueId = (id: number | string | undefined): number | null => {
    if (typeof id === "number" && Number.isFinite(id)) return id;
    if (typeof id === "string") {
      const parsed = Number.parseInt(id.trim(), 10);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const getErrorMessage = (err: unknown, fallback: string): string => {
    if (err instanceof Error && err.message.trim().length > 0) {
      return err.message;
    }
    const maybeAxios = err as {
      response?: { data?: { message?: unknown } };
      message?: unknown;
    };
    const responseMsg = maybeAxios.response?.data?.message;
    if (typeof responseMsg === "string" && responseMsg.trim().length > 0) return responseMsg;
    if (typeof maybeAxios.message === "string" && maybeAxios.message.trim().length > 0) {
      return maybeAxios.message;
    }
    return fallback;
  };

  const handleIssueSelect = (suggestion: Suggestion) => {
    setError(null);
    setSelectedIssue(suggestion);
    setIsProfileSelected(false);
    setResolvedIssueId(null);

    if (suggestion.type === "issue") {
      setResolvedIssueId(normalizeIssueId(suggestion.id));
      setIsResolvingIssue(false);
      return;
    }

    if (suggestion.type === "wiki") {
      const requestId = resolveRequestIdRef.current + 1;
      resolveRequestIdRef.current = requestId;
      setIsResolvingIssue(true);
      void (async () => {
        try {
          const createdOrExisting = await ensureIssue(suggestion);
          const issueId = normalizeIssueId(createdOrExisting?.id);
          if (resolveRequestIdRef.current !== requestId) return;
          if (!issueId) {
            setError(ui.resteepError);
            setResolvedIssueId(null);
            return;
          }
          setResolvedIssueId(issueId);
        } catch (err: unknown) {
          if (resolveRequestIdRef.current !== requestId) return;
          setResolvedIssueId(null);
          setError(getErrorMessage(err, ui.resteepError));
        } finally {
          if (resolveRequestIdRef.current === requestId) {
            setIsResolvingIssue(false);
          }
        }
      })();
    }
  };

  const handleProfileSelect = () => {
    const nextIsProfileSelected = !isProfileSelected;
    setIsProfileSelected(nextIsProfileSelected);
    setSelectedIssue(null);
    setResolvedIssueId(null);
    setIsResolvingIssue(false);
    setError(null);
    if (nextIsProfileSelected && privacy === "anonymous") {
      setPrivacy(
        currentUser?.properties?.privacy_content_default_audience?.value || "public"
      );
    }
  };

  const handleIssueChipClose = () => {
    setSelectedIssue(null);
    setResolvedIssueId(null);
    setIsResolvingIssue(false);
    setError(null);
  };

  const getTargetText = (): string => {
    if (isProfileSelected && currentUser) return ui.resteepProfileOption;
    if (selectedIssue) {
      return selectedIssue.name.length > 30
        ? `${selectedIssue.name.substring(0, 30)}\u2026`
        : selectedIssue.name;
    }
    return "";
  };

  const canSubmit = (): boolean => {
    if (isSubmitting) return false;
    if (isProfileSelected) return true;
    return typeof resolvedIssueId === "number" && Number.isFinite(resolvedIssueId) && !isResolvingIssue;
  };

  const handleSubmit = async () => {
    if (!canSubmit()) {
      if (isResolvingIssue) {
        setError(ui.resteepWaitIssue);
        return;
      }
      setError(ui.resteepSelectDestination);
      return;
    }

    if (!currentUser) {
      setError(ui.resteepLogin);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let targetEntityType: "user" | "issue";
      let targetEntityId: number;

      if (isProfileSelected) {
        targetEntityType = "user";
        targetEntityId = currentUser.id;
      } else {
        targetEntityType = "issue";
        if (!resolvedIssueId) {
          setError(ui.resteepSelectDestination);
          return;
        }
        targetEntityId = resolvedIssueId;
      }

      const RESTEEP_PRIVACY_MAP: Record<string, "public" | "private" | "anonymous"> = {
        public: "public",
        anonymous: "anonymous",
      };
      const apiPrivacy = RESTEEP_PRIVACY_MAP[privacy] ?? "public";

      const result = await createResteep({
        source_entity_type: sourceEntityType,
        source_entity_id: sourceEntityId,
        target_entity_type: targetEntityType,
        target_entity_id: targetEntityId,
        content: content || undefined,
        properties: { privacy: apiPrivacy },
        is_anonymous: apiPrivacy === "anonymous",
      });

      if (result.comment && privacy !== "anonymous") {
        try {
          await api.post(apiRoutes.votes.comment(result.comment.id), { value: 1 });
        } catch {
          // Non-critical; auto-vote failure is acceptable
        }
      }

      toast.success(ui.resteepSuccess);
      onClose();
    } catch (submitError: unknown) {
      setError(getErrorMessage(submitError, ui.resteepError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const targetText = getTargetText();
  const submitLabel = targetText
    ? ui.resteepToTarget.replace("{target}", targetText)
    : ui.resteepSelectDestination;

  const modalContent = (
    <div className="space-y-3 py-2 px-2">
      {/* Issue search */}
      <div className="space-y-3 pt-2">
        <IssueSearch
          onSelect={handleIssueSelect}
          placeholder={ui.resteepIssueSearchPlaceholder}
          inputType="add"
          autoFocus={true}
          excludeCompoundIssues={false}
          prepopulateData={selectedIssue?.type === "issue" ? selectedIssue : null}
          onEntityChipClose={handleIssueChipClose}
        />
        {selectedIssue?.type === "wiki" && (
          <div className="text-xs text-eoe-text-meta dark:text-eoe-dark-text-meta">
            {isResolvingIssue
              ? ui.resteepCreatingIssue
              : resolvedIssueId
                ? ui.resteepIssueCreated.replace("{id}", String(resolvedIssueId))
                : ui.resteepSelectIssue}
          </div>
        )}
      </div>

      {/* OR separator */}
      <div className="relative flex flex-row gap-2 items-center">
        <div className="w-full border-t border-eoe-border-main dark:border-eoe-dark-border-main" />
        <div className="relative flex flex-row flex-nowrap flex-shrink-0 justify-center text-sm">
          <span className="bg-eoe-bg dark:bg-eoe-dark-bg-modal text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {ui.resteepOrSeparator}
          </span>
        </div>
        <div className="w-full border-t border-eoe-border-main dark:border-eoe-dark-border-main" />
      </div>

      {/* User profile option */}
      <div className="space-y-3">
        <div
          onClick={handleProfileSelect}
          className={`cursor-pointer transition-none rounded-2xl ${
            isProfileSelected
              ? "ring-2 ring-eoe-border-main"
              : "border-dashed border border-opacity-75 border-eoe-border-main"
          }`}
        >
          {currentUser && (
            <EntityAvatar
              entity={currentUser}
              entityType="user"
              entityChip={true}
              showCloseChip={false}
              isInputField={false}
              showLink={false}
              className="w-fit"
              customSubtitle={ui.resteepProfileOption}
            />
          )}
        </div>
      </div>

      {/* Commentary input */}
      <div className="space-y-3">
        <TextEditor
          value={content}
          onChange={(value) => setContent(value)}
          editorType="comment"
          placeholder={ui.resteepAddThoughts}
        />
      </div>

      {/* Privacy control */}
      <div className="space-y-3">
        <EntityPrivacy
          privacy={privacy}
          onPrivacyChange={(newPrivacy) => setPrivacy(newPrivacy)}
          actorType="user"
          editMode={false}
          hideAnonymous={isProfileSelected}
        />
      </div>

      {/* Error */}
      {error && (
        <div className="text-sm text-warning-red dark:text-dark-warning-red bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Submit */}
      <div className="pt-4">
        <AddButton
          tooltipTitle={canSubmit() ? submitLabel : ui.resteepSelectDestination}
          buttonText={submitLabel}
          onClick={handleSubmit}
          className={`w-full ${canSubmit() ? "" : "opacity-50 cursor-not-allowed"}`}
          noPlus={isSubmitting || isResolvingIssue}
          isLoading={isSubmitting || isResolvingIssue}
        />
      </div>
    </div>
  );

  return (
    <>
      {userIsMobile ? (
        <Drawer open={isOpen} onOpenChange={onClose}>
          <DrawerContent title={ui.resteepModalTitle}>
            {modalContent}
          </DrawerContent>
        </Drawer>
      ) : (
        <Modal isOpen={isOpen} onClose={onClose} title={ui.resteepModalTitle}>
          {modalContent}
        </Modal>
      )}
    </>
  );
};

export default ResteepModal;
